import React, { useState, useEffect, useMemo } from 'react';
import ConfirmDeleteModal from './shared/ConfirmDeleteModal';
import Select from 'react-select';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { useWorkspaceRoster } from '../hooks/useWorkspaceRoster';
import useGoogleCalendarEvents from '../hooks/useGoogleCalendarEvents';
import useICalEvents from '../hooks/useICalEventsNew';
import { useOutlookEvents } from '../hooks/useOutlookEvents';
import './TeamOverview.css';

/**
 * ✅ ENHANCED TEAM CALENDAR WITH PROJECT ORGANIZATION
 *
 * 📅 Visual Style Matching:
 * ✅ 1. Use same calendar grid layout and CSS classes from user calendar
 * ✅ 2. Implement 7-day column layout with time slot rows or card-style event blocks
 * ✅ 3. Inherit spacing, border, and header styles from main calendar
 * ✅ 4. Ensure event cards match main user task cards (rounded, shadow, labels)
 *
 * 🧑‍🤝‍🧑 Team & Project Management:
 * ✅ 5. Multi-level filtering: Teams > Projects > Members
 * ✅ 6. @mention support in event descriptions
 * ✅ 7. Shared calendar views with permission controls
 * ✅ 8. Project-based event organization and filtering
 *
 * 🎯 Enhanced Features:
 * ✅ 9. Team creation and management interface
 * ✅ 10. Project assignment and tracking
 * ✅ 11. Cross-team collaboration support
 * ✅ 12. Event merging from multiple calendar sources
 */

const TeamCalendar = ({
  userSchedule = [],
  onEditTask,
  onDeleteTask,
  onAddTask,
}) => {
  const { currentUser } = useCurrentUser();
  const { members: users } = useWorkspaceRoster();
  const usersById = useMemo(() => new Map(users.map(u => [u.id, u])), [users]);
  // React-Select compatible selectedUsers array
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarRange, setCalendarRange] = useState('week'); // 'week' | 'nextWeek' | 'month'

  // ✅ ENHANCED TEAM/PROJECT MANAGEMENT STATE
  const [userTeams, setUserTeams] = useState([]);
  const [userProjects, setUserProjects] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [filterMode, setFilterMode] = useState('all'); // 'all', 'teams', 'projects', 'members'
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [newTeamData, setNewTeamData] = useState({ name: '', description: '', color: '#4F46E5' });
  const [newProjectData, setNewProjectData] = useState({ name: '', description: '', teamId: '', color: '#10B981' });

  // Mobile layout state
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [mobileDayDate, setMobileDayDate] = useState(new Date());
  const [mobileShowWeek, setMobileShowWeek] = useState(false);

  // React-Select options for team members
  const teamMemberOptions = users.map(user => ({
    value: user.id,
    label: `${user.name} (${user.email})`,
    user: user
  }));

  // Add "All Team Members" option
  const allMembersOption = {
    value: 'all',
    label: '👥 All Team Members',
    isAll: true
  };

  const selectOptions = [allMembersOption, ...teamMemberOptions];

  // Get selected team members for react-select
  const selectedTeamMembers = selectedUsers.includes('all') 
    ? [allMembersOption] 
    : teamMemberOptions.filter(option => selectedUsers.includes(option.value));

  // Get calendar events using hooks
  const { events: googleEvents = [] } = useGoogleCalendarEvents();
  
  // Get iCal events from user profile
  let userProfile = {};
  try { userProfile = JSON.parse(localStorage.getItem("userProfile") || "{}"); } catch { /* use empty default */ }
  const icalUrl = userProfile.integrations?.ical?.connected ? userProfile.integrations.ical.url : null;
  const { events: icalEvents = [] } = useICalEvents(icalUrl);
  
  // Get Outlook events
  const { events: outlookEvents = [] } = useOutlookEvents();

  useEffect(() => {
    // Initially show all users - set to 'all' option
    setSelectedUsers(['all']);
  }, []);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // React-Select team member selection handler
  const handleTeamMemberChange = (selectedOptions) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      setSelectedUsers([]);
      return;
    }

    // Check if "All Team Members" is selected
    const hasAllOption = selectedOptions.some(option => option.value === 'all');
    
    if (hasAllOption) {
      // If "All" is selected, only keep the "All" option
      setSelectedUsers(['all']);
    } else {
      // Individual team members selected
      const userIds = selectedOptions.map(option => option.value);
      setSelectedUsers(userIds);
    }
  };

  // Combine all events from different sources and add user context
  const getAllEvents = () => {
    const events = [];

    // ✅ Only process if we have selected users
    if (selectedUsers.length === 0) {
      console.log('🚫 No users selected, returning empty events array');
      return events;
    }

    // Determine which user IDs to include
    const userIdsToInclude = selectedUsers.includes('all') 
      ? users.map(u => u.id) 
      : selectedUsers;

    // Add user tasks (from scheduler)
    userSchedule.forEach(task => {
      // ✅ Add null checks for task data — accept date or datetime
      if (!task) return;
      const taskDateStr = task.date || (task.datetime ? task.datetime.slice(0, 10) : null);
      if (!taskDateStr) return;

      const assignedUsers = Array.isArray(task.assignedTeam)
        ? task.assignedTeam.map(id => usersById.get(id)).filter(Boolean)
        : task.assignedUsers || [currentUser];

      assignedUsers.forEach(user => {
        // ✅ FILTERING: Check if user is in selected team members
        if (!user || !user.id || !userIdsToInclude.includes(user.id)) {
          console.log(`🔍 Filtering out task "${task.title}" for user ${user?.name || 'unknown'} - not selected`);
          return;
        }

        // ✅ Validate date input before creating Date object
        const taskTimeStr = task.time || (task.datetime ? task.datetime.slice(11, 16) : '09:00');
        const taskDateTime = `${taskDateStr}T${taskTimeStr}`;
        const startDate = new Date(taskDateTime);
        const endDate = new Date(taskDateTime);
        
        // ✅ Check for valid dates
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          console.log(`🚫 Skipping task "${task.title}" - invalid date: ${taskDateTime}`);
          return;
        }

        const isOtherUsersPersonalTask = task.isPersonal && user.id !== currentUser?.id;
        console.log(`✅ Adding task "${task.title}" for user ${user.name} on ${taskDateStr}`);
        events.push({
          id: `task-${task.id}`,
          title: isOtherUsersPersonalTask ? 'Busy (Personal)' : (task.title || 'Untitled Task'),
          start: startDate,
          end: endDate,
          type: 'task',
          user: user,
          color: isOtherUsersPersonalTask ? '#b0bec5' : (user.color || '#007bff'),
          priority: isOtherUsersPersonalTask ? null : task.priority,
          details: isOtherUsersPersonalTask ? null : task.details,
          location: isOtherUsersPersonalTask ? null : task.location,
          meetingType: isOtherUsersPersonalTask ? null : task.meetingType,
          isPersonal: task.isPersonal,
          isHidden: isOtherUsersPersonalTask,
          originalTask: isOtherUsersPersonalTask ? null : task,
        });
      });
    });

    // Add Google Calendar events
    googleEvents.forEach(event => {
      // ✅ Add null checks for event data and check if current user is selected
      const isCurrentUserSelected = selectedUsers.includes('all') || selectedUsers.includes(currentUser.id);
      if (!event || !currentUser || !isCurrentUserSelected) {
        console.log(`🔍 Filtering out Google event "${event?.summary || 'unknown'}" - current user not selected`);
        return;
      }
      
      // ✅ Validate date inputs before creating Date objects
      const startDateTime = event.start?.dateTime || event.start?.date;
      const endDateTime = event.end?.dateTime || event.end?.date;
      
      if (!startDateTime || !endDateTime) return;
      
      const startDate = new Date(startDateTime);
      const endDate = new Date(endDateTime);
      
      // ✅ Check for valid dates
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        console.log(`🚫 Skipping Google event "${event.summary}" - invalid date`);
        return;
      }

      console.log(`✅ Adding Google event "${event.summary}" for current user`);
      events.push({
        id: `google-${event.id}`,
        title: event.summary || 'Google Event',
        start: startDate,
        end: endDate,
        type: 'google',
        user: currentUser,
        color: currentUser.color || '#4285f4',
        source: 'Google Calendar',
        originalEvent: event
      });
    });

    // Add iCal events
    icalEvents.forEach(event => {
      // ✅ Add null checks for event data and check if current user is selected
      const isCurrentUserSelected = selectedUsers.includes('all') || selectedUsers.includes(currentUser.id);
      if (!event || !currentUser || !isCurrentUserSelected) {
        console.log(`🔍 Filtering out iCal event "${event?.title || 'unknown'}" - current user not selected`);
        return;
      }
      
      // ✅ Validate date inputs before creating Date objects
      if (!event.start || !event.end) return;
      
      const startDate = new Date(event.start);
      const endDate = new Date(event.end);
      
      // ✅ Check for valid dates
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        console.log(`🚫 Skipping iCal event "${event.title}" - invalid date`);
        return;
      }

      console.log(`✅ Adding iCal event "${event.title}" for current user`);
      events.push({
        id: `ical-${event.id}`,
        title: event.title || 'iCal Event',
        start: startDate,
        end: endDate,
        type: 'ical',
        user: currentUser,
        color: currentUser.color || '#ff6b6b',
        source: 'iCal',
        originalEvent: event
      });
    });

    // Add Outlook events
    outlookEvents.forEach(event => {
      // ✅ Add null checks for event data and check if current user is selected
      const isCurrentUserSelected = selectedUsers.includes('all') || selectedUsers.includes(currentUser.id);
      if (!event || !currentUser || !isCurrentUserSelected) {
        console.log(`🔍 Filtering out Outlook event "${event?.subject || 'unknown'}" - current user not selected`);
        return;
      }
      
      // ✅ Validate date inputs before creating Date objects
      const startDateTime = event.start?.dateTime;
      const endDateTime = event.end?.dateTime;
      
      if (!startDateTime || !endDateTime) return;
      
      const startDate = new Date(startDateTime);
      const endDate = new Date(endDateTime);
      
      // ✅ Check for valid dates
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        console.log(`🚫 Skipping Outlook event "${event.subject}" - invalid date`);
        return;
      }

      console.log(`✅ Adding Outlook event "${event.subject}" for current user`);
      events.push({
        id: `outlook-${event.id}`,
        title: event.subject || 'Outlook Event',
        start: startDate,
        end: endDate,
        type: 'outlook',
        user: currentUser,
        color: currentUser.color || '#0078d4',
        source: 'Outlook',
        originalEvent: event
      });
    });

    console.log(`📊 Final events count: ${events.length} events for ${selectedUsers.size} selected users`);
    return events.sort((a, b) => a.start - b.start);
  };

  // Updated helper functions for react-select team selection
  const selectAllUsers = () => {
    setSelectedUsers(['all']); // Use the "all" option
  };

  const deselectAllUsers = () => {
    setSelectedUsers([]);
  };

  const getWeekDates = (date) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day; // First day of week (Sunday)
    start.setDate(diff);
    
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const current = new Date(start);
      current.setDate(start.getDate() + i);
      dates.push(current);
    }
    return dates;
  };

  const getEventsForDate = (date, events) => {
    // ✅ Add null checks for date parameter
    if (!date || isNaN(date.getTime())) return [];
    
    const dateStr = date.toDateString();
    return events.filter(event => {
      // ✅ Add null checks for event.start
      if (!event || !event.start || isNaN(event.start.getTime())) return false;
      return event.start.toDateString() === dateStr;
    });
  };

  // ✅ Add state for calendar interactions
  // eslint-disable-next-line no-unused-vars
  const [selectedDate, setSelectedDate] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [taskDeleteConfirm, setTaskDeleteConfirm] = useState(null);

  const EventCard = ({ event, compact = false }) => {
    // ✅ Add fallback for missing event data
    if (!event) return null;
    
    const eventTitle = event.title || 'Untitled Event';
    const eventUser = event.user || { name: 'Unknown User', color: '#666' };
    const eventColor = event.color || eventUser.color || '#007bff';
    
    // ✅ Add null check for event.start before calling time methods
    const eventTime = event.start && !isNaN(event.start.getTime()) 
      ? event.start.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        })
      : 'No time';

    return (
      <div 
        className={`event-card ${compact ? 'compact' : ''}`}
        style={{ 
          borderLeft: `4px solid ${eventColor}`,
          background: `${eventColor}10`
        }}
        // ✅ Add interactions: hovering and clicking
        onMouseEnter={() => setHoveredEvent(event)}
        onMouseLeave={() => setHoveredEvent(null)}
        onClick={() => {
          setSelectedEvent(event);
          setShowEventModal(true);
        }}
        title={`${eventTitle} - ${eventTime}`}
      >
        <div className="event-header">
          <span className="event-title">{eventTitle}</span>
          <span className="event-time">{eventTime}</span>
        </div>
        
        <div className="event-meta">
          <span className="event-user" style={{ color: eventColor }}>
            👤 {eventUser.name}
          </span>
          <span className="event-type">
            {event.type === 'task' ? '📋' : 
             event.type === 'google' ? '📅' :
             event.type === 'outlook' ? '📧' : '📆'} {event.source || event.type}
          </span>
        </div>

        {!compact && (
          <>
            {event.priority && (
              <div className={`priority-badge ${event.priority.toLowerCase()}`}>
                {event.priority} Priority
              </div>
            )}
            
            {event.location && (
              <div className="event-location">📍 {event.location}</div>
            )}
            
            {event.meetingType && (
              <div className="event-meeting-type">🎥 {event.meetingType}</div>
            )}
            
            {event.details && (
              <div className="event-details">{event.details}</div>
            )}
          </>
        )}
      </div>
    );
  };

  // ── Mobile day view ────────────────────────────────────────────────────────
  const MobileDayView = () => {
    const HOURS = Array.from({ length: 17 }, (_, i) => i + 6); // 6 AM – 10 PM

    const mobilePillDates = getWeekDates(mobileDayDate);
    const today = new Date();
    const isToday = mobileDayDate.toDateString() === today.toDateString();

    const dayEvents = getEventsForDate(mobileDayDate, allEvents)
      .sort((a, b) => a.start - b.start);

    function getEventsForHour(hour) {
      return dayEvents.filter(e => e.start.getHours() === hour);
    }

    function fmtHour(h) {
      if (h === 12) return '12 PM';
      return h > 12 ? `${h - 12} PM` : `${h} AM`;
    }

    const circleBtn = {
      width: 44, height: 44, borderRadius: '50%',
      border: '1px solid #dde6ef', background: '#f8f9fb',
      fontSize: '1.4rem', cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#0C2D48', flexShrink: 0, lineHeight: 1,
    };

    return (
      <div style={{ fontFamily: "'Raleway', sans-serif" }}>

        {/* ── Day navigation ───────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.65rem 1rem 0.4rem' }}>
          <button
            style={circleBtn}
            onClick={() => setMobileDayDate(d => { const n = new Date(d); n.setDate(n.getDate() - 1); return n; })}
          >‹</button>

          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#0C2D48', fontFamily: "'Poppins', sans-serif" }}>
              {mobileDayDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
            {isToday && (
              <div style={{ fontSize: '0.7rem', color: '#3498db', fontWeight: 700, marginTop: 2 }}>Today</div>
            )}
          </div>

          <button
            style={circleBtn}
            onClick={() => setMobileDayDate(d => { const n = new Date(d); n.setDate(n.getDate() + 1); return n; })}
          >›</button>
        </div>

        {/* ── Week pills ────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', padding: '0 0.75rem 0.6rem', gap: '0.15rem', overflowX: 'auto', scrollbarWidth: 'none' }}>
          {mobilePillDates.map(date => {
            const isSelected = date.toDateString() === mobileDayDate.toDateString();
            const isTodayPill = date.toDateString() === today.toDateString();
            const evCount = getEventsForDate(date, allEvents).length;
            return (
              <button
                key={date.toISOString()}
                onClick={() => setMobileDayDate(new Date(date))}
                style={{
                  flex: 1, minWidth: 36,
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  padding: '0.38rem 0.15rem 0.3rem',
                  borderRadius: 10, border: 'none',
                  background: isSelected ? '#0C2D48' : 'transparent',
                  cursor: 'pointer', gap: 2,
                }}
              >
                <span style={{ fontSize: '0.6rem', fontWeight: 700, color: isSelected ? 'rgba(255,255,255,0.75)' : '#aaa', textTransform: 'uppercase' }}>
                  {date.toLocaleDateString('en-GB', { weekday: 'short' }).charAt(0)}
                </span>
                <span style={{
                  fontWeight: 700, fontSize: '0.88rem',
                  color: isSelected ? '#fff' : isTodayPill ? '#0C2D48' : '#333',
                  width: 28, height: 28, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: isTodayPill && !isSelected ? 'rgba(228,230,155,0.85)' : 'transparent',
                }}>
                  {date.getDate()}
                </span>
                <div style={{
                  height: 5, width: 5, borderRadius: '50%',
                  background: evCount > 0
                    ? (isSelected ? 'rgba(255,255,255,0.7)' : '#3498db')
                    : 'transparent',
                }} />
              </button>
            );
          })}
        </div>

        {/* ── Timeline ─────────────────────────────────────────────────── */}
        <div style={{ padding: '0 0.75rem 0.75rem' }}>
          {dayEvents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📅</div>
              <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#bbb', marginBottom: '1.25rem' }}>
                No events today
              </div>
              {onAddTask && (
                <button
                  onClick={onAddTask}
                  style={{ padding: '0.6rem 1.5rem', background: '#0C2D48', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', fontFamily: "'Raleway', sans-serif" }}
                >
                  + Add Event
                </button>
              )}
            </div>
          ) : (
            <div>
              {HOURS.map(hour => {
                const hEvents = getEventsForHour(hour);
                return (
                  <div key={hour} style={{ display: 'flex', gap: '0.6rem', minHeight: hEvents.length > 0 ? 'auto' : 42 }}>
                    <div style={{ width: 44, flexShrink: 0, paddingTop: '0.2rem', textAlign: 'right', fontSize: '0.67rem', color: '#bbb', lineHeight: 1 }}>
                      {fmtHour(hour)}
                    </div>
                    <div style={{ flex: 1, borderTop: '1px solid #f0f0f0', paddingTop: '0.2rem', paddingBottom: hEvents.length > 0 ? '0.45rem' : 0 }}>
                      {hEvents.map(event => (
                        <div
                          key={event.id}
                          onClick={() => { setSelectedEvent(event); setShowEventModal(true); }}
                          style={{
                            background: event.color + '18',
                            borderLeft: `3px solid ${event.color}`,
                            borderRadius: 7,
                            padding: '0.45rem 0.65rem',
                            marginBottom: '0.3rem',
                            cursor: 'pointer',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.4rem' }}>
                            <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#0C2D48', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {event.title}
                            </span>
                            <div style={{ width: 22, height: 22, borderRadius: '50%', background: event.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.68rem', fontWeight: 700, flexShrink: 0 }}>
                              {event.user?.name?.charAt(0) || '?'}
                            </div>
                          </div>
                          <div style={{ fontSize: '0.73rem', color: '#888', marginTop: 2 }}>
                            {event.start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── View full week toggle ─────────────────────────────────────── */}
        <div style={{ textAlign: 'center', padding: '0 0 1rem' }}>
          <button
            onClick={() => setMobileShowWeek(v => !v)}
            style={{ background: 'none', border: '1px solid #dde6ef', borderRadius: 20, padding: '0.38rem 1.1rem', fontSize: '0.79rem', color: '#666', cursor: 'pointer', fontFamily: "'Raleway', sans-serif" }}
          >
            {mobileShowWeek ? 'Hide week view' : 'View full week'}
          </button>
        </div>

        {/* ── Horizontally scrollable week grid fallback ───────────────── */}
        {mobileShowWeek && (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', padding: '0 0.75rem 1rem' }}>
            <div style={{ minWidth: 640 }}>
              <CalendarGrid />
            </div>
          </div>
        )}
      </div>
    );
  };

  // ✅ Enhanced Calendar Grid Component with time slots
  const CalendarGrid = () => {
    const timeSlots = [
      { label: 'Morning', start: 6, end: 12 },
      { label: 'Afternoon', start: 12, end: 18 },
      { label: 'Evening', start: 18, end: 24 }
    ];

    const getEventsForTimeSlot = (date, timeSlot) => {
      const dayEvents = getEventsForDate(date, allEvents);
      return dayEvents.filter(event => {
        const eventHour = event.start.getHours();
        return eventHour >= timeSlot.start && eventHour < timeSlot.end;
      });
    };

    return (
      <div className="calendar-grid-container">
        {/* Calendar Header */}
        <div className="calendar-header">
          <div className="time-column-header">Time</div>
          {weekDates.map(date => {
            const isToday = date.toDateString() === new Date().toDateString();
            return (
              <div 
                key={date.toISOString()} 
                className={`calendar-day-header ${isToday ? 'today' : ''}`}
                onClick={() => {
                  setSelectedDate(date);
                  console.log('Selected date:', date);
                }}
              >
                <div className="day-name">
                  {date.toLocaleDateString('en-US', { weekday: 'short' })}
                </div>
                <div className="day-number">
                  {date.getDate()}
                </div>
                <div className="event-count-badge">
                  {getEventsForDate(date, allEvents).length}
                </div>
              </div>
            );
          })}
        </div>

        {/* Calendar Body with Time Slots */}
        <div className="calendar-body">
          {timeSlots.map(timeSlot => (
            <div key={timeSlot.label} className="calendar-row">
              <div className="time-slot-label">
                <span className="time-label">{timeSlot.label}</span>
                <span className="time-range">
                  {timeSlot.start}:00 - {timeSlot.end}:00
                </span>
              </div>
              
              {weekDates.map(date => {
                const slotEvents = getEventsForTimeSlot(date, timeSlot);
                const isToday = date.toDateString() === new Date().toDateString();
                
                return (
                  <div 
                    key={`${date.toISOString()}-${timeSlot.label}`}
                    className={`calendar-cell ${isToday ? 'today' : ''}`}
                    onClick={() => {
                      setSelectedDate(date);
                      console.log('Selected cell date:', date);
                    }}
                  >
                    {slotEvents.map(event => (
                      <div
                        key={event.id}
                        className="calendar-event"
                        style={{
                          backgroundColor: event.color,
                          color: '#fff'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedEvent(event);
                          setShowEventModal(true);
                        }}
                        onMouseEnter={() => setHoveredEvent(event)}
                        onMouseLeave={() => setHoveredEvent(null)}
                        title={`${event.title} - ${event.start.toLocaleTimeString('en-US', { 
                          hour: 'numeric', 
                          minute: '2-digit',
                          hour12: true 
                        })}`}
                      >
                        <div className="event-title-short">
                          {event.title.length > 20 ? `${event.title.substring(0, 20)}...` : event.title}
                        </div>
                        <div className="event-time-short">
                          {event.start.toLocaleTimeString('en-US', { 
                            hour: 'numeric', 
                            minute: '2-digit',
                            hour12: true 
                          })}
                        </div>
                        <div className="event-user-avatar" style={{ backgroundColor: event.user?.color || '#666' }}>
                          {event.user?.name?.charAt(0) || '?'}
                        </div>
                      </div>
                    ))}
                    
                    {slotEvents.length === 0 && (
                      <div className="empty-slot">
                        <span className="add-event-hint">+</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ✅ Event Details Modal Component
  const EventModal = () => {
    if (!showEventModal || !selectedEvent) return null;

    return (
      <div className="event-modal-overlay" onClick={() => setShowEventModal(false)}>
        <div className="event-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>{selectedEvent.title}</h3>
            <button 
              className="close-btn"
              onClick={() => setShowEventModal(false)}
            >
              ✕
            </button>
          </div>
          
          <div className="modal-body">
            <div className="event-detail-row">
              <strong>Time:</strong>
              <span>
                {selectedEvent.start.toLocaleString('en-US', { 
                  weekday: 'long',
                  month: 'long', 
                  day: 'numeric',
                  hour: 'numeric', 
                  minute: '2-digit',
                  hour12: true 
                })}
                {selectedEvent.end && selectedEvent.end.getTime() !== selectedEvent.start.getTime() && (
                  ` - ${selectedEvent.end.toLocaleTimeString('en-US', { 
                    hour: 'numeric', 
                    minute: '2-digit',
                    hour12: true 
                  })}`
                )}
              </span>
            </div>

            <div className="event-detail-row">
              <strong>Assigned to:</strong>
              <span style={{ color: selectedEvent.user?.color || '#666' }}>
                {selectedEvent.user?.name || 'Unknown User'}
              </span>
            </div>

            <div className="event-detail-row">
              <strong>Source:</strong>
              <span>{selectedEvent.source || selectedEvent.type}</span>
            </div>

            {selectedEvent.priority && (
              <div className="event-detail-row">
                <strong>Priority:</strong>
                <span className={`priority-badge ${selectedEvent.priority.toLowerCase()}`}>
                  {selectedEvent.priority}
                </span>
              </div>
            )}

            {selectedEvent.location && (
              <div className="event-detail-row">
                <strong>Location:</strong>
                <span>📍 {selectedEvent.location}</span>
              </div>
            )}

            {selectedEvent.meetingType && (
              <div className="event-detail-row">
                <strong>Meeting Type:</strong>
                <span>🎥 {selectedEvent.meetingType}</span>
              </div>
            )}

            {selectedEvent.details && (
              <div className="event-detail-row">
                <strong>Details:</strong>
                <p>{selectedEvent.details}</p>
              </div>
            )}
          </div>

          {selectedEvent.type === 'task' && selectedEvent.originalTask && (
            <div className="modal-actions">
              <button 
                className="btn-edit"
                onClick={() => {
                  if (onEditTask) onEditTask(selectedEvent.originalTask);
                  setShowEventModal(false);
                }}
              >
                ✏️ Edit Task
              </button>
              
              <button
                className="btn-delete"
                onClick={() => {
                  if (onDeleteTask) setTaskDeleteConfirm(selectedEvent.originalTask);
                }}
              >
                🗑️ Delete Task
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const allEvents = getAllEvents();
  const weekDates = (() => {
    if (calendarRange === 'nextWeek') {
      const d = new Date(currentDate);
      d.setDate(d.getDate() + 7);
      return getWeekDates(d);
    }
    return getWeekDates(currentDate);
  })();
  const monthDates = (() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];
    for (let d = 1; d <= daysInMonth; d++) days.push(new Date(year, month, d));
    return days;
  })();

  // ✅ DEBUG STEP: Temporarily console.log grouped tasks by date and user to verify filtering is correct
  useEffect(() => {
    console.log('🧪 DEBUG: Team Calendar Data Flow');
    console.log('- Current User:', currentUser);
    console.log('- Selected Users:', Array.from(selectedUsers));
    console.log('- User Schedule:', userSchedule);
    console.log('- Google Events:', googleEvents);
    console.log('- iCal Events:', icalEvents);
    console.log('- Outlook Events:', outlookEvents);
    console.log('- All Combined Events:', allEvents);
    
    // Group events by date for debugging
    const eventsByDate = {};
    allEvents.forEach(event => {
      const dateKey = event.start.toDateString();
      if (!eventsByDate[dateKey]) eventsByDate[dateKey] = [];
      eventsByDate[dateKey].push(event);
    });
    console.log('- Events Grouped by Date:', eventsByDate);
  }, [allEvents, currentUser, selectedUsers, userSchedule, googleEvents, icalEvents, outlookEvents]);

  // ── Month Grid Component ───────────────────────────────────────────────────
  const MonthGrid = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDayOfWeek = new Date(year, month, 1).getDay();
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const cells = [...Array(firstDayOfWeek).fill(null), ...monthDates];

    return (
      <div className="calendar-grid-container">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '2px solid #e0e0e0', background: '#f8f9fa' }}>
          {dayNames.map(d => (
            <div key={d} style={{ padding: '0.5rem', textAlign: 'center', fontWeight: 700, fontSize: '0.78rem', color: '#666' }}>{d}</div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {cells.map((date, i) => {
            if (!date) return <div key={i} style={{ minHeight: 80, borderRight: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }} />;
            const isToday = date.toDateString() === new Date().toDateString();
            const dayEvents = getEventsForDate(date, allEvents);
            return (
              <div key={date.toISOString()} style={{ minHeight: 80, padding: '0.35rem', borderRight: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0', background: isToday ? '#f0f7ff' : 'white' }}>
                <div style={{ fontSize: '0.8rem', fontWeight: isToday ? 700 : 500, color: isToday ? '#0C2D48' : '#333', marginBottom: '0.2rem' }}>
                  {date.getDate()}
                </div>
                {dayEvents.slice(0, 3).map(event => (
                  <div key={event.id} onClick={() => { setSelectedEvent(event); setShowEventModal(true); }} style={{ background: event.color, color: '#fff', borderRadius: 3, padding: '0.1rem 0.3rem', fontSize: '0.68rem', marginBottom: '0.1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                    {event.title}
                  </div>
                ))}
                {dayEvents.length > 3 && <div style={{ fontSize: '0.65rem', color: '#888', cursor: 'pointer' }}>+{dayEvents.length - 3} more</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="team-overview">
      <div className="team-overview-header">
        <div>
          <h2>Team Calendar</h2>
          <p>Collaborative view of all team schedules</p>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          {onAddTask && (
            <button
              onClick={onAddTask}
              style={{
                background: '#E4E69B', border: 'none', borderRadius: 8,
                padding: '0.55rem 1rem', fontFamily: "'Raleway', sans-serif",
                fontWeight: 700, fontSize: '0.88rem', color: '#0C2D48',
                cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              + Add Task
            </button>
          )}
          <div className="view-controls">
            <button
              className={viewMode === 'calendar' ? 'active' : ''}
              onClick={() => setViewMode('calendar')}
            >
              📅 Calendar
            </button>
            <button
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
            >
              📋 List
            </button>
          </div>
        </div>
      </div>

      {/* Team Member Selection */}
      <div className="team-selector" style={{
        marginBottom: "0.75rem",
        padding: "0.6rem 0.9rem",
        background: "white",
        borderRadius: "10px",
        border: "1px solid #e0e0e0",
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
      }}>
        <div style={{ flex: 1 }}>
        <Select
          isMulti
          value={selectedTeamMembers}
          onChange={handleTeamMemberChange}
          options={selectOptions}
          placeholder="🔍 Search and select team members..."
          closeMenuOnSelect={false}
          isSearchable={true}
          isClearable={true}
          styles={{
            control: (provided, state) => ({
              ...provided,
              border: state.isFocused ? '2px solid #1976d2' : '1px solid #ddd',
              borderRadius: '8px',
              padding: '0 0.25rem',
              minHeight: '36px',
              boxShadow: state.isFocused ? '0 0 0 3px rgba(25, 118, 210, 0.1)' : 'none',
              '&:hover': {
                borderColor: '#1976d2'
              }
            }),
            placeholder: (provided) => ({
              ...provided,
              color: '#666',
              fontSize: '0.9rem'
            }),
            multiValue: (provided, { data }) => ({
              ...provided,
              backgroundColor: data.isAll ? '#e8f5e8' : '#e3f2fd',
              borderRadius: '16px',
              border: data.isAll ? '1px solid #c8e6c9' : '1px solid #bbdefb'
            }),
            multiValueLabel: (provided, { data }) => ({
              ...provided,
              color: data.isAll ? '#2e7d32' : '#1565c0',
              fontWeight: '500',
              fontSize: '0.85rem',
              padding: '0.2rem 0.5rem'
            }),
            multiValueRemove: (provided, { data }) => ({
              ...provided,
              color: data.isAll ? '#2e7d32' : '#1565c0',
              '&:hover': {
                backgroundColor: data.isAll ? '#4caf50' : '#1976d2',
                color: 'white'
              }
            }),
            option: (provided, state) => ({
              ...provided,
              backgroundColor: state.isSelected 
                ? '#1976d2' 
                : state.isFocused 
                  ? '#f5f5f5' 
                  : 'white',
              color: state.isSelected ? 'white' : '#333',
              padding: '0.75rem 1rem',
              cursor: 'pointer',
              '&:active': {
                backgroundColor: state.isSelected ? '#1976d2' : '#e3f2fd'
              }
            }),
            menu: (provided) => ({
              ...provided,
              borderRadius: '8px',
              border: '1px solid #ddd',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              zIndex: 9999
            }),
            menuList: (provided) => ({
              ...provided,
              padding: '0.5rem 0',
              maxHeight: '200px'
            })
          }}
          formatOptionLabel={(option) => {
            if (option.isAll) {
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ 
                    fontSize: '1.2rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    👥
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontWeight: '600', 
                      fontSize: '0.9rem',
                      color: '#2e7d32'
                    }}>
                      All Team Members
                    </div>
                    <div style={{ 
                      fontSize: '0.8rem', 
                      color: '#666'
                    }}>
                      Show calendar events for all {users.length} team members
                    </div>
                  </div>
                </div>
              );
            }
            
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: option.user.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '0.85rem',
                    fontWeight: '700',
                    flexShrink: 0
                  }}
                >
                  {option.user.name.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontWeight: '600', 
                    fontSize: '0.9rem',
                    marginBottom: '0.1rem'
                  }}>
                    {option.user.name}
                    {option.user.id === currentUser.id && (
                      <span style={{ 
                        marginLeft: '0.5rem',
                        fontSize: '0.7rem', 
                        padding: '0.2rem 0.4rem', 
                        background: '#e8f5e8', 
                        color: '#2e7d32', 
                        borderRadius: '4px',
                        fontWeight: '700',
                        textTransform: 'uppercase'
                      }}>
                        YOU
                      </span>
                    )}
                  </div>
                  <div style={{ 
                    fontSize: '0.8rem', 
                    color: '#666'
                  }}>
                    {option.user.email}
                  </div>
                </div>
              </div>
            );
          }}
        />
        
        </div>
        <button
          onClick={selectAllUsers}
          style={{ padding: "0.3rem 0.75rem", fontSize: "0.8rem", background: "#f5f5f5", border: "1px solid #ddd", borderRadius: "6px", cursor: "pointer", fontWeight: "500", whiteSpace: "nowrap" }}
        >
          Select All
        </button>
        <button
          onClick={deselectAllUsers}
          style={{ padding: "0.3rem 0.75rem", fontSize: "0.8rem", background: "#f5f5f5", border: "1px solid #ddd", borderRadius: "6px", cursor: "pointer", fontWeight: "500", whiteSpace: "nowrap" }}
        >
          Clear All
        </button>
      </div>

      {/* Range selector */}
      <div style={{ display: "flex", gap: "0.4rem", marginBottom: "0.6rem", justifyContent: "center" }}>
        {[['week', 'This Week'], ['nextWeek', 'Next Week'], ['month', 'Month']].map(([range, label]) => (
          <button
            key={range}
            onClick={() => setCalendarRange(range)}
            style={{
              padding: "0.3rem 0.85rem", borderRadius: 20, fontSize: "0.8rem", fontWeight: 600,
              border: calendarRange === range ? "1px solid #0C2D48" : "1px solid #e0e0e0",
              background: calendarRange === range ? "#0C2D48" : "#f8f8f8",
              color: calendarRange === range ? "#fff" : "#666",
              cursor: "pointer",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Date Navigation — desktop / month view only */}
      {(!isMobile || calendarRange === 'month') && (
        <div className="date-navigation">
          <button
            onClick={() => {
              const newDate = new Date(currentDate);
              if (calendarRange === 'month') newDate.setMonth(newDate.getMonth() - 1);
              else newDate.setDate(newDate.getDate() - 7);
              setCurrentDate(newDate);
            }}
            className="nav-btn"
          >
            ← {calendarRange === 'month' ? 'Previous Month' : 'Previous Week'}
          </button>

          <h3>
            {calendarRange === 'month'
              ? currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
              : `${weekDates[0].toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - ${weekDates[6].toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
            }
          </h3>

          <button
            onClick={() => {
              const newDate = new Date(currentDate);
              if (calendarRange === 'month') newDate.setMonth(newDate.getMonth() + 1);
              else newDate.setDate(newDate.getDate() + 7);
              setCurrentDate(newDate);
            }}
            className="nav-btn"
          >
            {calendarRange === 'month' ? 'Next Month' : 'Next Week'} →
          </button>
        </div>
      )}

      {/* Content */}
      {viewMode === 'calendar' ? (
        <div className="team-calendar-view">
          {isMobile && calendarRange !== 'month'
            ? <MobileDayView />
            : calendarRange === 'month'
              ? <MonthGrid />
              : <CalendarGrid />
          }
        </div>
      ) : (
        <div className="team-list-view">
          <div className="events-by-date">
            {(calendarRange === 'month' ? monthDates : weekDates).map(date => {
              const dayEvents = getEventsForDate(date, allEvents);
              if (dayEvents.length === 0) return null;
              
              return (
                <div key={date.toISOString()} className="date-section">
                  <h3 className="date-header">
                    {date.toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      month: 'long', 
                      day: 'numeric',
                      year: 'numeric'
                    })}
                    <span className="event-count">({dayEvents.length} events)</span>
                  </h3>
                  
                  <div className="date-events">
                    {dayEvents.map(event => (
                      <EventCard key={event.id} event={event} />
                    ))}
                  </div>
                </div>
              );
            })}
            
            {allEvents.length === 0 && (
              <div className="no-events-message">
                <h3>No events for selected team members</h3>
                <p>Try selecting different team members or check a different date range.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Statistics */}
      <div
        className="team-stats"
        style={isMobile ? { gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '1rem' } : {}}
      >
        <div className="stat-card" style={isMobile ? { padding: '1rem' } : {}}>
          <h4>Total Events</h4>
          <span className="stat-number">{allEvents.length}</span>
        </div>

        <div className="stat-card" style={isMobile ? { padding: '1rem' } : {}}>
          <h4>Team Members</h4>
          {isMobile ? (
            <div style={{ fontSize: '1rem', fontWeight: 700, color: '#3498db', marginTop: '0.25rem' }}>
              {selectedUsers.includes('all') ? users.length : selectedUsers.length} member{(selectedUsers.includes('all') ? users.length : selectedUsers.length) !== 1 ? 's' : ''}
            </div>
          ) : (
            <span className="stat-number">
              {selectedUsers.includes('all') ? users.length : selectedUsers.length}/{users.length}
            </span>
          )}
        </div>

        <div className="stat-card" style={isMobile ? { padding: '1rem', gridColumn: '1 / -1' } : {}}>
          <h4>This Week</h4>
          <span className="stat-number">
            {allEvents.filter(e => {
              const eventDate = e.start;
              return eventDate >= weekDates[0] && eventDate <= weekDates[6];
            }).length}
          </span>
        </div>
      </div>

      {/* Event Details Modal */}
      <EventModal />

      {/* Floating + button (mobile only) */}
      {isMobile && onAddTask && (
        <button
          onClick={onAddTask}
          aria-label="Add event"
          style={{
            position: 'fixed',
            bottom: '1.5rem',
            right: '1.5rem',
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: '#0C2D48',
            color: '#fff',
            border: 'none',
            fontSize: '1.65rem',
            lineHeight: 1,
            cursor: 'pointer',
            boxShadow: '0 4px 18px rgba(12,45,72,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 200,
          }}
        >
          +
        </button>
      )}

      <style jsx="true">{`
        .calendar-grid-container {
          display: flex;
          flex-direction: column;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          overflow: hidden;
          background: white;
        }

        .calendar-header {
          display: grid;
          grid-template-columns: 120px repeat(7, 1fr);
          border-bottom: 2px solid #e0e0e0;
          background: #f8f9fa;
        }

        .time-column-header {
          padding: 12px;
          font-weight: bold;
          border-right: 1px solid #e0e0e0;
          background: #f1f3f4;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .calendar-day-header {
          padding: 12px 8px;
          text-align: center;
          border-right: 1px solid #e0e0e0;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .calendar-day-header:hover {
          background: #e8f0fe;
        }

        .calendar-day-header.today {
          background: #e3f2fd;
          font-weight: bold;
        }

        .day-name {
          font-size: 12px;
          color: #666;
          margin-bottom: 4px;
        }

        .day-number {
          font-size: 18px;
          font-weight: bold;
          margin-bottom: 4px;
        }

        .event-count-badge {
          background: #4285f4;
          color: white;
          border-radius: 12px;
          padding: 2px 8px;
          font-size: 11px;
          display: inline-block;
        }

        .calendar-body {
          display: flex;
          flex-direction: column;
        }

        .calendar-row {
          display: grid;
          grid-template-columns: 120px repeat(7, 1fr);
          min-height: 100px;
          border-bottom: 1px solid #e0e0e0;
        }

        .time-slot-label {
          padding: 12px;
          border-right: 1px solid #e0e0e0;
          background: #f8f9fa;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .time-label {
          font-weight: bold;
          font-size: 14px;
          margin-bottom: 4px;
        }

        .time-range {
          font-size: 11px;
          color: #666;
        }

        .calendar-cell {
          padding: 4px;
          border-right: 1px solid #e0e0e0;
          position: relative;
          cursor: pointer;
          transition: background-color 0.2s;
          min-height: 96px;
          overflow: hidden;
        }

        .calendar-cell:hover {
          background: #f5f5f5;
        }

        .calendar-cell.today {
          background: #e8f0fe;
        }

        .calendar-event {
          margin-bottom: 2px;
          padding: 4px 6px;
          border-radius: 4px;
          font-size: 11px;
          cursor: pointer;
          position: relative;
          transition: transform 0.2s, box-shadow 0.2s;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .calendar-event:hover {
          transform: scale(1.02);
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          z-index: 10;
        }

        .event-title-short {
          font-weight: bold;
          line-height: 1.2;
        }

        .event-time-short {
          font-size: 10px;
          opacity: 0.9;
        }

        .event-user-avatar {
          position: absolute;
          top: 2px;
          right: 2px;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 8px;
          font-weight: bold;
          color: white;
        }

        .empty-slot {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: #ccc;
          font-size: 24px;
          opacity: 0;
          transition: opacity 0.2s;
        }

        .calendar-cell:hover .empty-slot {
          opacity: 1;
        }

        .add-event-hint {
          cursor: pointer;
          transition: color 0.2s;
        }

        .add-event-hint:hover {
          color: #4285f4;
        }

        .event-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .event-modal {
          background: white;
          border-radius: 8px;
          padding: 0;
          max-width: 500px;
          width: 90%;
          max-height: 80vh;
          overflow-y: auto;
        }

        .modal-header {
          padding: 20px;
          border-bottom: 1px solid #e0e0e0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #f8f9fa;
        }

        .modal-body {
          padding: 20px;
        }

        .event-detail-row {
          margin-bottom: 12px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .event-detail-row strong {
          color: #333;
          font-size: 14px;
        }

        .event-detail-row span, .event-detail-row p {
          color: #666;
          font-size: 14px;
        }

        .modal-actions {
          padding: 20px;
          border-top: 1px solid #e0e0e0;
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .btn-edit, .btn-delete {
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          transition: background-color 0.2s;
        }

        .btn-edit {
          background: #4285f4;
          color: white;
        }

        .btn-edit:hover {
          background: #3367d6;
        }

        .btn-delete {
          background: #ea4335;
          color: white;
        }

        .btn-delete:hover {
          background: #d33b2c;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: background-color 0.2s;
        }

        .close-btn:hover {
          background: #e0e0e0;
        }

        .priority-badge {
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: bold;
          text-transform: uppercase;
        }

        .priority-badge.high {
          background: #ffebee;
          color: #c62828;
        }

        .priority-badge.medium {
          background: #fff3e0;
          color: #ef6c00;
        }

        .priority-badge.low {
          background: #e8f5e8;
          color: #2e7d32;
        }
      `}</style>
      <ConfirmDeleteModal
        open={taskDeleteConfirm !== null}
        onClose={() => setTaskDeleteConfirm(null)}
        onConfirm={() => { onDeleteTask(taskDeleteConfirm.id); setShowEventModal(false); }}
        itemType="task"
        itemName={taskDeleteConfirm?.title || ''}
        warningText="This will permanently delete this task."
      />
    </div>
  );
};

export default TeamCalendar;
